/******* Angular Resourse *******/
import { Injectable } from '@angular/core';
import { Router, RoutesRecognized } from "@angular/router";

/******* Shared Resourse *******/
import { UtilsService } from './utils.service';

/******* Plug-In Resourse *******/
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

/******* API Resourse *******/
import { Groups } from '@api';
import { GroupRegisterRequestBody, RoleRequest } from '@core/types';

@Injectable({
    providedIn: 'root',
})
export class GroupService {

    // public
    public currentGroupList: Observable<any>;

    // private
    private currentGroupListSubject: BehaviorSubject<any>;
    private currentGroupMapping: any = {};

    /**
     * Constructor
     *
     * @param {Groups} _groups
     * @param {UtilsService} _utilsService
     * @param {Router} _router
     */
    constructor(private _groups: Groups,
        private _utilsService: UtilsService,
        private _router: Router) {

        this.currentGroupListSubject = new BehaviorSubject<any>(null);
        this.currentGroupList = this.currentGroupListSubject.asObservable();

        this._router.events.pipe(filter(event => event instanceof RoutesRecognized), take(1)).subscribe((e: RoutesRecognized) => {
            const url = e.urlAfterRedirects;
            if (url.indexOf("/pages/") >= 0) this.getGrouops();
        });
    }

    //  Accessors
    // -----------------------------------------------------------------------------------------------------

    // getter: currentGroupListValue
    public get currentGroupListValue(): any {
        return this.currentGroupListSubject.value;
    }

    // getter: currentGroupMappingValue
    public get currentGroupMappingValue(): any {
        return this.currentGroupMapping;
    }

    // Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get Groups List
     * 
     * @param resolve 
     * @param rejects 
     * @param forwardData
     */
    private getGrouops(resolve = null, reject = null, forwardData = null): void {
        this._groups.getGroupsWithDevice().subscribe((returnData) => {
            returnData.data = returnData.data.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()));
            this.currentGroupListSubject.next(returnData);
            this.currentGroupMapping = {};
            returnData.data.forEach((group) => { this.currentGroupMapping[group._id] = group.name });
            if (resolve) resolve(forwardData ? forwardData : returnData);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to Load Group Data', error);
            if (reject) reject(error);
        });
    }

    /**
     * create new group
     * 
     * @param {GroupRegisterRequestBody} inputData 
     * @param resolve 
     * @param rejects 
     */
    private _createGroup(inputData: GroupRegisterRequestBody, resolve = null, rejects = null): void {
        this._groups.createGroup(inputData).subscribe((returnData) => {
            this.getGrouops(resolve, rejects, returnData);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to Create Group Data', error);
            if (rejects) rejects(error);
        });
    }

    /**
     * Update the Group by id
     * 
     * @param id 
     * @param inputData 
     * @param resolve 
     * @param rejects 
     */
    private _updateGroup(id: string, inputData: GroupRegisterRequestBody, resolve = null, rejects = null): void {
        this._groups.updateGroup(id, inputData).subscribe(() => {
            this.getGrouops(resolve, rejects);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to Update Group Data', error);
            if (rejects) rejects(error);
        });
    }

    /**
     * assign multiple device to the Group
     * 
     * @param id 
     * @param inputData 
     * @param resolve 
     * @param rejects 
     */
    private _assignGroup(id: string, inputData: string[], resolve = null, rejects = null): void {
        this._groups.assignGroupToDevice(id, inputData).subscribe(() => {
            this.getGrouops(resolve, rejects);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to Assign Group', error);
            if (rejects) rejects(error);
        });
    }

    /**
     * unassign multiple device from Group
     * 
     * @param inputData 
     * @param resolve 
     * @param rejects 
     */
    private _unassignGroup(inputData: string[], resolve = null, rejects = null): void {
        this._groups.unassignGroupFromDevice(inputData).subscribe(() => {
            this.getGrouops(resolve, rejects);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to Unassign Group', error);
            if (rejects) rejects(error);
        });
    }

    /**
     * delete group
     * 
     * @param {string} id 
     * @param resolve 
     * @param rejects 
     */
    private _deleteGroup(id: string, resolve = null, rejects = null): void {
        this._groups.deleteGroup(id).subscribe(() => {
            this.getGrouops(resolve, rejects);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to Delete Group Data', error);
            if (rejects) rejects(error);
        });
    }

    /**
     * get group's roles
     * 
     * @param {string} id 
     * @param resolve 
     * @param rejects 
     */
    private _getGroupRoles(id: string, resolve = null, rejects = null): void {
        this._groups.getGroupRoles(id).subscribe((returnData) => {
            if (resolve) resolve(returnData.data);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to get group roles', error);
            if (rejects) rejects(error);
        });
    }

    /**
     * save new group's role
     * 
     * @param {string} id 
     * @param {RoleRequest} inputData
     * @param resolve 
     * @param rejects 
     */
    private _saveGroupRole(id: string, inputData: RoleRequest, resolve = null, rejects = null): void {
        this._groups.saveGroupRole(id, inputData).subscribe(() => {
            this.getGrouops(resolve, rejects);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to save group role', error);
            if (rejects) rejects(error);
        });
    }

    /**
     * update group's role
     * 
     * @param {string} id 
     * @param {string} roleId 
     * @param {RoleRequest} inputData
     * @param resolve 
     * @param rejects 
     */
    private _updateGroupRole(id: string, roleId: string, inputData: RoleRequest, resolve = null, rejects = null): void {
        this._groups.updateGroupRole(id, roleId, inputData).subscribe(() => {
            this.getGrouops(resolve, rejects);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to update group role', error);
            if (rejects) rejects(error);
        });
    }

    /**
     * update group's role
     * 
     * @param {string} id 
     * @param {string} roleId 
     * @param resolve 
     * @param rejects 
     */
    private _deleteGroupRole(id: string, roleId: string, resolve = null, rejects = null): void {
        this._groups.deleteGroupRole(id, roleId).subscribe(() => {
            this.getGrouops(resolve, rejects);
        }, (error) => {
            this._utilsService.alert('error', 'Fail to delete group role', error);
            if (rejects) rejects(error);
        });
    }

    // Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Refresh Group
     */
    public refreshGroups(): Promise<any> {
        return new Promise((resolve, rejects) => this.getGrouops(resolve, rejects));
    }

    /**
     * Create Group
     * 
     * @param inputData
     * @returns 
     */
    public createGroup(inputData: any): Promise<any> {
        return new Promise((resolve, rejects) => this._createGroup(inputData, resolve, rejects));
    }

    /**
     * Update Group
     * 
     * @param id
     * @param inputData
     * @returns 
     */
    public updateGroup(id: string, inputData: any): Promise<any> {
        return new Promise((resolve, rejects) => this._updateGroup(id, inputData, resolve, rejects));
    }

    /**
     * assign multiple device to one group
     * 
     * @param id
     * @param inputData
     * @returns 
     */
    public assignGroup(id: string, inputData: string[]): Promise<any> {
        return new Promise((resolve, rejects) => this._assignGroup(id, inputData, resolve, rejects));
    }

    /**
     * unassign multiple device's group
     * 
     * @param inputData
     * @returns 
     */
    public unassignGroup(inputData: string[]): Promise<any> {
        return new Promise((resolve, rejects) => this._unassignGroup(inputData, resolve, rejects));
    }

    /**
     * Delete Group By Id
     * 
     * @param id
     * @returns {Promise<any>}
     */
    public deleteGroup(id: string): Promise<any> {
        return new Promise((resolve, rejects) => this._deleteGroup(id, resolve, rejects));
    }

    /**
     * Get Group Role By Id
     * 
     * @param id
     * @returns {Promise<any>}
     */
    public getGroupRoles(id: string): Promise<any> {
        return new Promise((resolve, rejects) => this._getGroupRoles(id, resolve, rejects));
    }

    /**
     * Save New Group Role By Id
     * 
     * @param {string} id
     * @param {RoleRequest} inputData
     * @returns {Promise<any>}
     */
    public saveGroupRole(id: string, inputData: RoleRequest): Promise<any> {
        return new Promise((resolve, rejects) => this._saveGroupRole(id, inputData, resolve, rejects));
    }

    /**
     * Update Group Role By Id
     * 
     * @param {string} id
     * @param {string} roleId
     * @param {RoleRequest} inputData
     * @returns {Promise<any>}
     */
    public updateGroupRole(id: string, roleId: string, inputData: RoleRequest): Promise<any> {
        return new Promise((resolve, rejects) => this._updateGroupRole(id, roleId, inputData, resolve, rejects));
    }

    /**
     * Delete Group Role By Id
     * 
     * @param {string} id
     * @param {string} roleId
     * @returns {Promise<any>}
     */
    public deleteGroupRole(id: string, roleId: string): Promise<any> {
        return new Promise((resolve, rejects) => this._deleteGroupRole(id, roleId, resolve, rejects));
    }

    /**
     * Clear the Current User Data
     */
    public clearAllInfo(): void {
        this.currentGroupListSubject.next(null);
    }
}